import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { entityService } from "@/services/breeze/EntityService";
import { IEntity } from "@/types/breeze/Entity";
import { roleAndPermissionService } from "@/services/breeze/RoleAndPermissionService";
import { Permission, Role } from "@/types/breeze/RoleAndPermission";
import { userService } from "@/services/breeze/UserService";
import { UsersPageQuery, UserWithRole } from "@/types/breeze/User";
import { PaginatedResult } from "@/types/breeze/Any";
import { tenantService } from "@/services/breeze/TenantService";
import { Tenant, TenantPlan } from "@/types/breeze/Tenant";
import { groupService } from "@/services/breeze/GroupService";
import { Group } from "@/types/breeze/Group";

export function useTenant(tenantId: number) {
  return useQuery<Tenant>({
    queryKey: ["tenant", tenantId],
    queryFn: () => tenantService.getTenantById(tenantId),
    enabled: tenantId > 0,
  });
}

export function useTenantPlan() {
  return useQuery<TenantPlan>({
    queryKey: ["tenant-plan"],
    queryFn: () => tenantService.getPlan(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useEntities(includeSystem: boolean = false) {
  return useQuery<IEntity[]>({
    queryKey: ["entities", { includeSystem }],
    queryFn: () => entityService.getAll(includeSystem),
  });
}

export function usePermissions() {
  return useQuery<Permission[]>({
    queryKey: ["permissions"],
    queryFn: () => roleAndPermissionService.getPermissions(),
  });
}

export function useMyPermissions() {
  return useQuery<Permission[]>({
    queryKey: ["my-permissions"],
    queryFn: () => userService.getMyPermissions(),
  });
}

export function useRoles() {
  return useQuery<Role[]>({
    queryKey: ["roles"],
    queryFn: () => roleAndPermissionService.getRoles(),
  });
}

export function useUsers() {
  return useQuery<UserWithRole[]>({
    queryKey: ["users"],
    queryFn: () => userService.getAllUsers(),
  });
}

export function useUsersPage(query: UsersPageQuery) {
  return useQuery<PaginatedResult<UserWithRole>>({
    queryKey: ["users", "page", query],
    queryFn: () => userService.getUsersPage(query),
    placeholderData: keepPreviousData,
  });
}

export function useGroups() {
  return useQuery<Group[]>({
    queryKey: ["groups"],
    queryFn: () => groupService.listGroups(),
  });
}

// Add more common queries as needed
